import { computed, watch } from 'vue'
import { useColorMode } from '@vueuse/core'
import { useRoute } from 'vue-router'
import useConfigStore from '@/store/config.js'
import {
  FORCE_DARK_ROUTES,
  SWITCHABLE_THEME_ROUTES,
  isForceDarkRoute,
  isSwitchableThemeRoute,
} from './themeRoute.js'

const THEME_LIGHT = 'light'
const THEME_DARK = 'dark'
const THEME_VALUES = [THEME_LIGHT, THEME_DARK]
const DARK_BODY_BACKGROUND = '#1C1F2C' // body 的背景色
const LIGHT_BODY_BACKGROUND = '#fff'
const applyHtmlThemeClass = (theme) => {
  const htmlDom = document.querySelector('html')
  if (!htmlDom) return
  htmlDom.classList.remove(THEME_LIGHT, THEME_DARK)
  htmlDom.classList.add(theme)
}

const useAppColorMode = () => {
  const route = useRoute()
  const configStore = useConfigStore()
  const colorMode = useColorMode({
    selector: 'html',
    attribute: 'class',
    storageKey: 'theme',
    initialValue: configStore.themePreference,
    modes: {
      [THEME_LIGHT]: THEME_LIGHT,
      [THEME_DARK]: THEME_DARK,
    },
    // 禁用 useColorMode 的自动 onChanged 回调，因为主题类的应用由 effectiveTheme watch 统一控制。
    // effectiveTheme 综合考虑了路由（强制 dark/可切换）和用户偏好，确保主题切换逻辑集中管理。
    onChanged: () => {},
  })

  const effectiveTheme = computed(() => {
    if (isForceDarkRoute(route.path)) {
      return THEME_DARK
    }
    if (isSwitchableThemeRoute(route.path)) {
      return configStore.themePreference
    }
    return THEME_LIGHT
  })

  // 实现 localStorage 和 colorMode 的双向绑定
  watch(
    () => colorMode.store.value,
    (themePreference) => {
      if (themePreference !== configStore.themePreference) {
        configStore.themePreference = themePreference
      }
    },
    { immediate: true }
  )

  watch(
    () => configStore.themePreference,
    (themePreference) => {
      if (themePreference !== colorMode.store.value) {
        colorMode.store.value = themePreference
      }
    }
  )

  // 改变 html 的 class 和 body 的背景色和 configStore 的 theme
  watch(
    effectiveTheme,
    (theme) => {
      applyHtmlThemeClass(theme)
      configStore.setResolvedTheme(theme)
      document.body.style.backgroundColor = theme === THEME_DARK ? DARK_BODY_BACKGROUND : LIGHT_BODY_BACKGROUND
    },
    { immediate: true }
  )

  return {
    colorMode,
    effectiveTheme,
    isDarkRoute: computed(() => isForceDarkRoute(route.path)),
    isSwitchableThemeRoute: computed(() => isSwitchableThemeRoute(route.path)),
  }
}

export { useAppColorMode, THEME_LIGHT, THEME_DARK, THEME_VALUES, FORCE_DARK_ROUTES, SWITCHABLE_THEME_ROUTES, isForceDarkRoute, isSwitchableThemeRoute, applyHtmlThemeClass }
